import { ArrowUp, Trophy, Users } from 'lucide-react'

import { MOCK_TOURNAMENT } from '@/features/marketing/mockData/tournament'

/**
 * Tournament pot card mockup. Mirrors the real `TournamentPotCard`
 * on the EventDetail page — big prize-pool figure with a per-place
 * breakdown and the host's "Add to pool" CTA visible.
 *
 * Numbers come from MOCK_TOURNAMENT so adjusting the tournament
 * fixture cascades here.
 */
export function PrizePoolMockup() {
  const t = MOCK_TOURNAMENT
  const potPounds = (t.prizePoolPence / 100).toFixed(0)
  const entryPounds = (t.entryFeePence / 100).toFixed(0)
  // 50 / 30 / 20 default split.
  const first  = Math.round(t.prizePoolPence * 0.5)  // £40
  const second = Math.round(t.prizePoolPence * 0.3)  // £24
  const third  = Math.round(t.prizePoolPence * 0.2)  // £16
  return (
    <div className="p-6">
      {/* Hero — prize pool callout */}
      <div className="relative overflow-hidden rounded-xl border border-warning/40 bg-gradient-to-br from-warning/15 via-bg/20 to-bg/10 p-5">
        <div className="flex items-center justify-between">
          <p className="font-display flex items-center gap-1.5 text-[10px] font-bold tracking-[0.22em] text-warning uppercase">
            <Trophy className="size-3" aria-hidden />
            Prize pool
          </p>
          <span className="font-display rounded-full border border-warning/40 bg-warning/10 px-2 py-0.5 text-[10px] font-bold tracking-[0.15em] text-warning uppercase">
            LIVE
          </span>
        </div>
        <p className="font-display mt-2 text-5xl font-black tracking-tight text-fg">
          £{potPounds}
        </p>
        <p className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-fg-muted">
          <Users className="size-3" aria-hidden />
          {t.attendeeCount} / {t.attendeeCap} entrants
          <span aria-hidden>·</span>
          £{entryPounds} entry
        </p>

        {/* Split breakdown */}
        <div className="mt-4 grid grid-cols-3 gap-2">
          {[
            { rank: '1st', amount: first, accent: 'border-warning/50 bg-warning/10 text-warning' },
            { rank: '2nd', amount: second, accent: 'border-fg-subtle/40 bg-fg-subtle/5 text-fg' },
            { rank: '3rd', amount: third, accent: 'border-fg-subtle/30 bg-bg/40 text-fg-muted' },
          ].map((p) => (
            <div
              key={p.rank}
              className={`rounded-md border px-2 py-1.5 text-center ${p.accent}`}
            >
              <p className="font-display text-[9px] font-bold tracking-[0.18em] uppercase">
                {p.rank}
              </p>
              <p className="font-display mt-0.5 text-base font-black">
                £{(p.amount / 100).toFixed(0)}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Owner top-up CTA */}
      <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-border bg-bg/40 p-3">
        <div className="min-w-0">
          <p className="font-display text-[10px] font-bold tracking-[0.18em] text-fg-subtle uppercase">
            Owner top-up
          </p>
          <p className="mt-0.5 text-xs text-fg-muted">
            Sponsor the pool from your own balance.
          </p>
        </div>
        <button
          type="button"
          className="font-display inline-flex shrink-0 items-center gap-1 rounded-md border border-warning/40 bg-warning/15 px-3 py-1.5 text-[11px] font-bold tracking-[0.12em] text-warning uppercase"
        >
          <ArrowUp className="size-3" aria-hidden />
          Add to pool
        </button>
      </div>

      {/* Trust strip */}
      <p className="font-display mt-4 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[10px] tracking-[0.18em] text-fg-subtle uppercase">
        <span>Stripe Connect</span>
        <span aria-hidden>·</span>
        <span>1% platform fee</span>
        <span aria-hidden>·</span>
        <span>Auto payouts to winners</span>
      </p>
    </div>
  )
}
